import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
// P4 — Patient_Report.js / Clinician_Report.js replaced by one combined
// component. 2026-10-22: the two old components were deleted outright —
// they were dead code with no import/route anywhere, and their own
// upload flow already 422'd (missing newly-mandatory address fields).
// Their old URLs still redirect below instead of 404ing for anyone with
// a bookmark.
import ReportGeneration from "./components/Report_Generation";
import Home from "./components/Home";
import ReportsDashboard from "./components/ReportsDashboard";
import RequisitionConversion from "./components/RequisitionConversion";
import AuditDashboard from "./components/AuditDashboard"; // P10
import RcmDashboard from "./components/RcmDashboard"; // 2026-10-04 — see ADMIN_RCM_CONSOLIDATION_PLAN.md
import UserDashboard from "./components/UserDashboard"; // 2026-10-04 — real counterpart to labariumaiportal's own /admin "User Dashboard" page
import IdentityLinking from "./components/IdentityLinking"; // Order a Test, Part 12
import LabOrdersDashboard from "./components/LabOrdersDashboard"; // Lab Orders — admin, cross-lab
import ClientFacingDashboard from "./components/ClientFacingDashboard"; // 2026-10-21 — client-facing verification dashboard
import StaffLabOrders from "./components/StaffLabOrders"; // Lab Orders — staff, own-lab-only
import Login from "./components/Login";
import AdminLogin from "./components/AdminLogin";
import AdminDashboard from "./components/AdminDashboard";
import ChangePassword from "./components/ChangePassword";
import ThemeContextProvider from "./components/ThemeContext";

function PrivateRoute({ children }) {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/login" replace />;
}

// 2026-09-30 addition — separate from PrivateRoute: gates the admin
// dashboard on "admin_token", not the regular staff "token".
function AdminRoute({ children }) {
  const adminToken = localStorage.getItem("admin_token");
  const location = useLocation();
  // 2026-10-08 fix — real gap, reported directly: a deep link like
  // /admin/audit-log?source=portal&identity=... (the cross-app audit
  // click-through from labariumaiportal's AdminDashboard.js) got silently
  // dropped whenever the admin wasn't already logged in — this redirect
  // used to lose the original path entirely, always landing on the plain
  // /admin dashboard after login instead of back where the link pointed.
  // Carry the full path+query through as router state so AdminLogin.js
  // can send them back to it once they're signed in.
  return adminToken ? children : (
    <Navigate to="/admin-login" replace state={{ from: location.pathname + location.search }} />
  );
}

function App() {
  return (
    <ThemeContextProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/change-password" element={<PrivateRoute><ChangePassword /></PrivateRoute>} />
          <Route path="/" element={<PrivateRoute><Home /></PrivateRoute>} />
          <Route path="/generate_report" element={<PrivateRoute><ReportGeneration /></PrivateRoute>} />
          {/* P4 — old URLs redirect to the new combined flow, not a 404 for anyone with a bookmark */}
          <Route path="/patient_report" element={<Navigate to="/generate_report" replace />} />
          <Route path="/clinician_report" element={<Navigate to="/generate_report" replace />} />
          <Route path="/reports" element={<PrivateRoute><ReportsDashboard /></PrivateRoute>} />
          <Route path="/requisition_conversion" element={<PrivateRoute><RequisitionConversion /></PrivateRoute>} />
          {/* 2026-10-04 — /summary (SummaryView.js, P7) removed: per explicit
              instruction, this staff-facing report-generation app should have
              no patient-summaries feature at all — that lives only on the
              patient/clinician-facing labariumaiportal side (MySummaries.js).
              SummaryView.js kept in the repo, unrouted (unlike
              Patient_Report.js/Clinician_Report.js, deleted 2026-10-22 as
              confirmed dead code). */}
          <Route path="/admin/audit-log" element={<AdminRoute><AuditDashboard /></AdminRoute>} /> {/* P10 — reachable via AdminDashboard's own link now, not a Home card */}
          <Route path="/admin/rcm" element={<AdminRoute><RcmDashboard /></AdminRoute>} /> {/* 2026-10-04 — reachable via AdminDashboard's own "RCM Dashboard" button */}
          <Route path="/admin/users" element={<AdminRoute><UserDashboard /></AdminRoute>} /> {/* 2026-10-04 — reachable via AdminDashboard's own "User Dashboard" button */}
          <Route path="/admin/identity-linking" element={<AdminRoute><IdentityLinking /></AdminRoute>} /> {/* Order a Test, Part 12 */}
          <Route path="/admin/lab-orders" element={<AdminRoute><LabOrdersDashboard /></AdminRoute>} /> {/* Lab Orders — admin, cross-lab */}
          <Route path="/admin/client-facing" element={<AdminRoute><ClientFacingDashboard /></AdminRoute>} /> {/* 2026-10-21 — client-facing verification dashboard */}
          <Route path="/lab-orders" element={<PrivateRoute><StaffLabOrders /></PrivateRoute>} /> {/* Lab Orders — staff, own-lab-only */}
          {/* 2026-10-14 fix — real gap, reported directly: no catch-all
              route existed, so a typo'd/stale/removed deep link matched
              nothing in this <Routes> and rendered a blank page instead of
              a real error — even though the Firebase hosting rewrite
              (firebase.json) correctly serves index.html for any unknown
              path, React Router itself had nothing to show once index.html
              loaded. Redirect to Home (which itself redirects unauthenticated
              users to /login via PrivateRoute) rather than a bare "404 page"
              component — matches this app's existing convention of
              redirecting stale URLs (see /patient_report, /clinician_report
              above) instead of dead-ending the user. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeContextProvider>
  );
}

export default App;
